<template>
    <div>
        <div class="col-12">
            <div class="text-h4 text-weight-medium">
                {{ $t('Shift Management') }}
            </div>
            <div class="page-description">
                {{
                    $t('Configure work shifts for {facilityName}.', {
                        facilityName: this.facility?.name
                    })
                }}
            </div>
        </div>
        <q-table
            :rows="shifts"
            :columns="columns"
            v-model:pagination="pagination"
            :rows-per-page-options="[pagination.rowsPerPage]"
            :hide-bottom="shifts.length > 0"
            :no-data-label="
                $t(
                    'This facility has no shifts yet. Click on Add Shift to add a shift.'
                )
            "
        >
            <template v-slot:header="props">
                <q-tr :props="props">
                    <q-th
                        v-for="col in props.cols"
                        :key="col.name"
                        :props="props"
                        class="bg-accent text-white"
                        >{{ $t(col.label) }}</q-th
                    >
                </q-tr>
            </template>
            <!-- Name and time range columns -->
            <template v-slot:body-cell-name="props">
                <q-td :props="props">
                    <q-input
                        class="bg-white q-mt-md shift-name-input"
                        v-if="props.row.isEdit"
                        v-model="props.row.name"
                        :label="$t('Name')"
                        :rules="[val => val !== '' || $t('Enter a name')]"
                        :hint="$t('Name of the shift')"
                        :error="props.row.errorMessage !== ''"
                        :error-message="props.row.errorMessage"
                        type="text"
                        outlined
                        dense
                        color="accent"
                    />
                    <div v-else>{{ props.row.name }}</div>
                </q-td>
            </template>
            <template v-slot:body-cell-start="props">
                <q-td :props="props">
                    <q-input
                        v-if="props.row.isEdit"
                        v-model="props.row.start_hour"
                        @click="handleShowTimePicker(props.row, 'start_hour')"
                        :label="$t('Start')"
                        :hint="$t('Start time of the shift')"
                        mask="##:##"
                        class="q-mt-md shift-start-input"
                        outlined
                        dense
                        color="accent"
                    ></q-input>
                    <div v-else>
                        {{ formatTime(props.row.start_hour) }}
                    </div>
                </q-td>
            </template>
            <template v-slot:body-cell-end="props">
                <q-td :props="props">
                    <q-input
                        v-if="props.row.isEdit"
                        v-model="props.row.end_hour"
                        @click="handleShowTimePicker(props.row, 'end_hour')"
                        :label="$t('End')"
                        :hint="$t('End time of the shift')"
                        mask="##:##"
                        class="q-mt-md shift-end-input"
                        outlined
                        dense
                        color="accent"
                    ></q-input>
                    <div v-else>
                        {{ formatTime(props.row.end_hour) }}
                    </div>
                </q-td>
            </template>

            <!-- Day columns -->
            <template v-slot:body-cell-sunday="props">
                <q-td :props="props">
                    <q-checkbox
                        v-model="props.row.sunday"
                        :disable="!props.row.isEdit"
                        :color="props.row.isEdit ? 'accent' : 'grey'"
                    />
                </q-td>
            </template>
            <template v-slot:body-cell-monday="props">
                <q-td :props="props">
                    <q-checkbox
                        v-model="props.row.monday"
                        :disable="!props.row.isEdit"
                        :color="props.row.isEdit ? 'accent' : 'grey'"
                    />
                </q-td>
            </template>
            <template v-slot:body-cell-tuesday="props">
                <q-td :props="props">
                    <q-checkbox
                        v-model="props.row.tuesday"
                        :disable="!props.row.isEdit"
                        :color="props.row.isEdit ? 'accent' : 'grey'"
                    />
                </q-td>
            </template>
            <template v-slot:body-cell-wednesday="props">
                <q-td :props="props">
                    <q-checkbox
                        v-model="props.row.wednesday"
                        :disable="!props.row.isEdit"
                        :color="props.row.isEdit ? 'accent' : 'grey'"
                    />
                </q-td>
            </template>
            <template v-slot:body-cell-thursday="props">
                <q-td :props="props">
                    <q-checkbox
                        v-model="props.row.thursday"
                        :disable="!props.row.isEdit"
                        :color="props.row.isEdit ? 'accent' : 'grey'"
                    />
                </q-td>
            </template>
            <template v-slot:body-cell-friday="props">
                <q-td :props="props">
                    <q-checkbox
                        v-model="props.row.friday"
                        :disable="!props.row.isEdit"
                        :color="props.row.isEdit ? 'accent' : 'grey'"
                    />
                </q-td>
            </template>
            <template v-slot:body-cell-saturday="props">
                <q-td :props="props">
                    <q-checkbox
                        v-model="props.row.saturday"
                        :disable="!props.row.isEdit"
                        :color="props.row.isEdit ? 'accent' : 'grey'"
                    />
                </q-td>
            </template>

            <!-- Action column -->
            <template v-slot:body-cell-actions="props">
                <q-td :props="props">
                    <div class="row q-gutter-md justify-center">
                        <q-btn
                            v-if="props.row.isEdit"
                            @click="save(props.row)"
                            :disable="
                                $userCannot('create', 'shifts') ||
                                $userCannot('edit', 'shifts')
                            "
                            class="table__action-button"
                            color="positive"
                            icon="fas fa-check"
                            outline
                        >
                            <q-tooltip>{{ $t('Save shift') }}</q-tooltip>
                        </q-btn>
                        <q-btn
                            v-if="props.row.isEdit"
                            @click="cancelEdit(props.row)"
                            class="table__action-button"
                            icon="fas fa-times"
                            color="negative"
                            outline
                        >
                            <q-tooltip>{{ $t('Cancel edit') }}</q-tooltip>
                        </q-btn>
                        <q-btn
                            v-if="!props.row.isEdit"
                            :disable="$userCannot('edit', 'shifts')"
                            :color="
                                $userCannot('edit', 'shifts')
                                    ? 'grey'
                                    : 'accent'
                            "
                            @click="edit(props.row)"
                            class="table__action-button"
                            icon="fas fa-edit"
                            outline
                        >
                            <q-tooltip v-if="!props.row.isEdit">
                                {{
                                    $userCannot('edit', 'shifts')
                                        ? $t(
                                              'You do not have permission to perform this action.'
                                          )
                                        : $t('Edit shift')
                                }}
                            </q-tooltip>
                        </q-btn>
                        <q-btn
                            v-if="props.row.isEdit"
                            :disable="$userCannot('delete', 'shifts')"
                            @click="handleRemove(props.row)"
                            class="table__action-button"
                            color="negative"
                            icon="fas fa-trash-alt"
                        >
                            <q-tooltip>{{ $t('Delete shift') }}</q-tooltip>
                        </q-btn>
                    </div>
                </q-td>
            </template>
        </q-table>

        <div align="right" class="q-mt-md">
            <q-btn
                @click="addShift"
                :label="$t('Add shift')"
                :disable="$userCannot('create', 'shifts') || this.isAdding"
                :color="$userCannot('create', 'shifts') ? 'grey' : 'accent'"
                class="q-mb-md"
            ></q-btn>
        </div>

        <q-dialog v-model="timePicker.show">
            <q-card>
                <time-picker
                    v-model="timePicker.value"
                    :minute-interval="'15'"
                ></time-picker>
                <div class="row q-ma-md">
                    <div align="right" class="col">
                        <q-btn
                            @click="timePicker.show = false"
                            :label="$t('Cancel')"
                            class="q-mr-sm"
                            color="white"
                            text-color="primary"
                            outline
                        />
                        <q-btn
                            @click="handleTimePickerSelect"
                            :label="$t('Select')"
                            color="primary"
                        />
                    </div>
                </div>
            </q-card>
        </q-dialog>
    </div>
</template>

<script>
import BreadcrumbsManager from '@mixins/BreadcrumbsManager.vue';
import { mapGetters } from 'vuex';
import TimePicker from '../components/TimePicker.vue';
import { mapActions, mapState } from 'pinia';
import { useSharedStore } from '@stores/shared.js';
import { useAuthStore } from '@stores/auth';
import { useFacilitiesStore } from '@stores/facilities.js';

export default {
    name: 'ShiftList',
    components: {
        TimePicker
    },
    mixins: [BreadcrumbsManager],
    props: {
        facilityId: {
            type: [Number, String],
            required: true
        }
    },
    async created() {
        // get shifts
        this.getShifts();
    },
    mounted() {
        // set breadcrumbs
        this.setBreadcrumbs([
            {
                label: this.$t('Facilities'),
                route: {
                    name: 'facility_list'
                }
            },
            {
                label: this.facility.name,
                route: {
                    name: 'facility_dashboard',
                    params: {
                        facilityId: this.facility?.id
                    }
                }
            },
            {
                label: this.$t('Settings'),
                route: {
                    name: 'facility_settings',
                    params: {
                        facilityId: this.facility?.id
                    }
                }
            },
            {
                label: this.$t('Shift Management')
            }
        ]);
    },
    data() {
        return {
            columns: [
                {
                    name: 'name',
                    field: 'name',
                    label: 'Name',
                    align: 'left',
                    headerStyle: 'min-width: 150px;'
                },
                {
                    name: 'start',
                    field: 'start',
                    label: 'Start',
                    align: 'left',
                    headerStyle: 'min-width: 80px;'
                },
                {
                    name: 'end',
                    field: 'end',
                    label: 'End',
                    align: 'left',
                    headerStyle: 'min-width: 80px;'
                },
                {
                    name: 'sunday',
                    label: 'Sun',
                    align: 'center',
                    required: true
                },
                {
                    name: 'monday',
                    label: 'Mon',
                    align: 'center',
                    required: true
                },
                {
                    name: 'tuesday',
                    label: 'Tue',
                    align: 'center',
                    required: true
                },
                {
                    name: 'wednesday',
                    label: 'Wed',
                    align: 'center',
                    required: true
                },
                {
                    name: 'thursday',
                    label: 'Thu',
                    align: 'center',
                    required: true
                },
                {
                    name: 'friday',
                    label: 'Fri',
                    align: 'center',
                    required: true
                },
                {
                    name: 'saturday',
                    label: 'Sat',
                    align: 'center',
                    required: true
                },
                {
                    name: 'actions',
                    label: 'Actions',
                    align: 'center',
                    required: true
                }
            ],

            shifts: [],
            rowId: 0,
            timePicker: {
                show: false,
                row: null,
                key: null,
                value: null
            },

            pagination: {
                sortBy: '',
                descending: false,
                page: 1,
                rowsPerPage: 30,
                rowsNumber: null
            }
        };
    },
    computed: {
        ...mapGetters({
            getFacility: 'facilities/getFacility'
        }),
        ...mapState(useAuthStore, {
            userLocale: 'getUserLocale',
            user: 'user'
        }),
        ...mapState(useFacilitiesStore, {
            getShiftsByFacility: 'getShiftsByFacility'
        }),
        /**
         * Checks if there is any shift in the list that is currently being added.
         * A shift is considered to be in the process of being added if its `id` is `null`.
         * @returns {boolean} - Returns `true` if there is at least one shift with `id` as `null`, otherwise `false`.
         */
        isAdding() {
            return this.shifts.some(shift => shift.id === null);
        },
        /**
         * Retrieves the facility information based on the current facility ID.
         * @returns {Object} The facility information.
         */
        facility() {
            return this.getFacility(this.facilityId);
        }
    },
    methods: {
        ...mapActions(useSharedStore, ['beginLoading', 'stopLoading']),
        ...mapActions(useFacilitiesStore, ['loadShiftsAction']),
        /**
         * Format time in hh:mm:ss to hh:mm
         * @return {String}
         */
        formatTime(time) {
            const [hour, minute, second] = time.split(':');
            return `${hour}:${minute}`;
        },
        /**
         * Give a row an id.
         *
         * When adding rows, shift.is cannot be used as reference to the row as there could be multiple. So removing a row in the table
         * using shift id causes issues. Also, we need to know the reference to the row to update it's shift.id from null during create.
         * @return {Number}
         */
        setRowId() {
            this.rowId++;
            return this.rowId;
        },
        /**
         * Add a shift in the table.
         */
        addShift() {
            // check if all table rows are saved
            const isAllRowsSaved = this.shifts.every(
                shift => shift.id !== null
            );

            // only allow a single create row
            if (isAllRowsSaved) {
                // set shift values
                const shift = {
                    rowId: this.setRowId(),
                    id: null,
                    name: '',
                    start_hour: '00:00:00',
                    end_hour: '12:00:00',
                    sunday: true,
                    monday: true,
                    tuesday: true,
                    wednesday: true,
                    thursday: true,
                    friday: true,
                    saturday: true,
                    facility: this.facilityId,
                    isEdit: true,
                    error: false,
                    errorMessage: ''
                };
                this.shifts.push(shift);
            }
        },
        /**
         * Refresh local shifts data from the store.
         * This syncs the component's local state with the Pinia store.
         */
        async refreshLocalShifts() {
            const shiftsData = this.getShiftsByFacility(this.facilityId);

            if (shiftsData) {
                // Clear current shifts
                this.shifts = [];

                // Add UI-specific properties to each shift
                for (const shift of shiftsData) {
                    this.shifts.push({
                        ...shift,
                        rowId: this.setRowId(),
                        isEdit: false,
                        errorMessage: '',
                        error: false
                    });
                }
            }
        },
        /**
         * Get shifts from the store or server.
         * First checks if shifts exist in the store, otherwise fetches from API.
         */
        async getShifts() {
            // Check if shifts already exist in the store
            let shiftsData = this.getShiftsByFacility(this.facilityId);

            // If shifts haven't been fetched yet (null), fetch from API
            if (shiftsData === null) {
                // begin loading
                this.beginLoading(`${this.$t('Loading shifts')}...`);
                try {
                    shiftsData = await this.loadShiftsAction(this.facilityId);
                } catch (error) {
                    console.error('error: ', error);
                    this.$notifyError(
                        this.$t(
                            'Failed to fetch shifts, please try again later.'
                        ),
                        error
                    );
                    this.stopLoading();
                    return;
                } finally {
                    this.stopLoading();
                }
            }

            // Add UI-specific properties to each shift
            // Deep copy to prevent mutations from affecting the store
            for (const shift of shiftsData) {
                this.shifts.push({
                    ...shift,
                    isEdit: false,
                    rowId: this.setRowId(),
                    errorMessage: '',
                    error: false
                });
            }
        },
        /**
         * Update shift.
         * @param {Object} shift
         */
        async update(shift, row) {
            // begin loading
            this.beginLoading(`${this.$t('Updating shift')}...`);
            // updates shift
            await this.$axios
                .put(
                    `/facilities/${this.facilityId}/shifts/${shift.id}/`,
                    shift
                )
                .then(async response => {
                    this.$notifySuccess(this.$t('Shift updated'));
                    row.isEdit = false;
                    // Reload shifts from API to update store
                    await this.loadShiftsAction(this.facilityId);
                    // Refresh local shifts data
                    await this.refreshLocalShifts();
                })
                .catch(error => {
                    this.$notifyError(
                        this.$t(
                            'Failed to update shift, please try again later.'
                        ),
                        error
                    );
                    // keep shift as editable
                    row.isEdit = true;
                })
                .finally(() => {
                    this.stopLoading();
                });
        },
        /**
         * Create shift.
         * @param {Object} shift
         */
        async create(shift, row) {
            // begin loading
            this.beginLoading(`${this.$t('Creating shift')}...`);

            // create shift
            await this.$axios
                .post(`/facilities/${this.facilityId}/shifts/`, shift)
                .then(async response => {
                    this.$notifySuccess(this.$t('Shift created'));
                    // close shift creation
                    row.isEdit = false;
                    // Reload shifts from API to update store
                    await this.loadShiftsAction(this.facilityId);
                    // Refresh local shifts data
                    await this.refreshLocalShifts();
                })
                .catch(error => {
                    this.$notifyError(
                        this.$t(
                            'Failed to create shift, please try again later.'
                        ),
                        error
                    );
                    // keep shift as editable
                    row.isEdit = true;
                })
                .finally(() => {
                    this.stopLoading();
                });
        },
        /**
         * Delete shift.
         * @param {Number} shiftId
         */
        async remove(shiftId) {
            // begin loading
            this.beginLoading(`${this.$t('Deleting shift')}...`);

            // delete shift
            await this.$axios
                .delete(`/facilities/${this.facilityId}/shifts/${shiftId}`)
                .then(async response => {
                    // notify user
                    this.$notifySuccess(this.$t('Shift deleted.'));
                    // Reload shifts from API to update store
                    await this.loadShiftsAction(this.facilityId);
                    // Refresh local shifts data
                    await this.refreshLocalShifts();
                })
                .catch(error => {
                    this.$notifyError(
                        this.$t(
                            'Failed to delete shift, please try again later.'
                        ),
                        error
                    );
                })
                .finally(() => {
                    this.stopLoading();
                });
        },
        /**
         * Handle row edit.
         * Keep track of the fields before editing
         * @param {Object} row - Row data.
         */
        edit(row) {
            // save a backup of the row
            row['backup'] = {};
            // for all properties in row
            for (const property in row) {
                // not copy 'backup' and 'isEdit' props
                if (property !== 'backup' && property !== 'isEdit') {
                    // set row["backup"][prop] = deep copy of row[prop]
                    row['backup'][property] = JSON.parse(
                        JSON.stringify(row[property])
                    );
                }
            }
            // open the editing mode
            row.isEdit = true;
        },
        /**
         * Handle row cancel edit.
         * Assign the values that before editing to the fields.
         * @param {Object} row - Row data.
         */
        cancelEdit(row) {
            // get shift data (drop frontend fields)
            const { shiftId, isEdit, ...shift } = row;
            // restore the row fields
            for (let prop in row['backup']) {
                // deep copy the prop from backup
                row[prop] = JSON.parse(JSON.stringify(row['backup'][prop]));
            }
            // close the editing mode
            row.isEdit = false;

            // remove row from table
            if (!shift.id) {
                const shiftIndex = this.shifts.findIndex(
                    shift => shift.id === shiftId
                );
                this.shifts.splice(shiftIndex, 1);
            }
        },
        /**
         * Build shift payload from a table row
         * @param {Object} row
         * @return {Object} shift data
         */
        getShiftData(row) {
            return {
                id: row.id,
                name: row.name,
                start_hour: row.start_hour,
                end_hour: row.end_hour,
                facility: row.facility,
                sunday: row.sunday,
                monday: row.monday,
                tuesday: row.tuesday,
                wednesday: row.wednesday,
                thursday: row.thursday,
                friday: row.friday,
                saturday: row.saturday
            };
        },
        /**
         * Handle row save.
         * @param {Object} row - Row data.
         */
        async save(row) {
            // get shift data (drop frontend fields)
            const shift = this.getShiftData(row);
            // validate payload
            const isValid = this.validate(shift);
            if (isValid) {
                if (shift.id) {
                    await this.update(shift, row);
                } else {
                    await this.create(shift, row);
                }
            }
        },
        /**
         * Validate shift.
         * @param {Object} shift
         * @return {Boolean} True if shift is valid
         */
        validate(shift) {
            let isValid = true;

            // validate name
            if (shift.name === '' || shift.name === null) {
                shift.errorMessage = this.$t('Enter a name');
                this.$notifyError(this.$t('A shift name must be provided.'));
                isValid = false;
                return isValid;
            }

            // must check at least one day
            let days = [
                shift.sunday,
                shift.monday,
                shift.tuesday,
                shift.wednesday,
                shift.thursday,
                shift.friday,
                shift.saturday
            ];
            if (
                days.every(day => {
                    return day === false;
                })
            ) {
                this.$notifyError(
                    this.$t('At least one day of the week must be selected.')
                );
                isValid = false;
                return isValid;
            }

            return isValid;
        },
        /**
         * Handle remove shift.
         * @param {Object} row
         */
        async handleRemove(row) {
            // get shift id
            const shiftId = row.id;

            await this.$confirmDelete(
                this.$t('Are you sure you want to delete this shift?')
            ).onOk(async () => {
                // check whether to delete shift from server
                if (shiftId) {
                    await this.remove(shiftId);
                    // remove() already refreshes local shifts from store, no need to manually splice
                } else {
                    // Only manually remove from table if shift was never saved to server
                    const shiftIndex = this.shifts.findIndex(
                        shift => shift.rowId === row.rowId
                    );
                    if (shiftIndex !== -1) {
                        this.shifts.splice(shiftIndex, 1);
                    }
                }
            });
        },
        /**
         * Handle show time picker. Sets the active row for the time picker and the key and value to use when confirmed.
         * @param {String} row - Active row to edit.
         * @param {String} key - Key name to edit (i.e. 'start_hour' or 'end_hour')
         */
        handleShowTimePicker(row, key) {
            this.timePicker.show = true;
            this.timePicker.row = row;
            this.timePicker.key = key;
            // if the row already has a time set, use it
            const time = this.timePicker.row[this.timePicker.key];

            if (time) {
                this.timePicker.value = time;
            }
        },
        /**
         * Set the selected time to the row.
         */
        handleTimePickerSelect() {
            this.timePicker.row[this.timePicker.key] = this.timePicker.value;
            this.timePicker.show = false;
        }
    }
};
</script>

<style lang="scss" scoped>
:deep(.shift-header) {
    font-size: 17px;
}

:deep(.shift-cell) {
    font-weight: 700;
}

:deep(.action-header) {
    font-size: 17px;
}
</style>
